import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, Loader2, Save, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { api } from './api';

const BAND_COLOR = {
  'On track': 'text-emerald-700 border-emerald-200 bg-emerald-50',
  'Below typical': 'text-amber-700 border-amber-200 bg-amber-50',
  'Slightly above typical': 'text-amber-700 border-amber-200 bg-amber-50',
  'Above typical': 'text-rose-700 border-rose-200 bg-rose-50',
};

const Growth = () => {
  const navigate = useNavigate();
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [recent, setRecent] = useState([]);
  const [error, setError] = useState('');

  const refreshRecent = useCallback(async () => {
    try { setRecent((await api.recentGrowth()).data || []); } catch { /* ignore */ }
  }, []);
  useEffect(() => { refreshRecent(); }, [refreshRecent]);

  const canSubmit = parseFloat(height) >= 20 && parseFloat(height) <= 220 && parseFloat(weight) >= 1 && parseFloat(weight) <= 120;

  const submit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const r = await api.postGrowth({ height_cm: parseFloat(height), weight_kg: parseFloat(weight) });
      setResult(r);
      await refreshRecent();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not save.');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50/40 via-white to-white pb-24" data-testid="child-growth">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <button onClick={() => navigate('/childrens-health')} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-4">
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <header className="mb-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Growth & development</h1>
              <p className="text-xs text-gray-500 mt-0.5">Update height and weight to see trend bands.</p>
            </div>
          </div>
        </header>

        <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5 mb-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Height (cm)</label>
              <input
                type="number"
                inputMode="decimal"
                step="0.1"
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                placeholder="e.g. 92"
                className="w-full text-base font-medium border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-emerald-400"
                data-testid="growth-height"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Weight (kg)</label>
              <input
                type="number"
                inputMode="decimal"
                step="0.1"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder="e.g. 13.5"
                className="w-full text-base font-medium border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-emerald-400"
                data-testid="growth-weight"
              />
            </div>
          </div>
          {error && <p className="text-sm text-rose-700">{error}</p>}
          <button
            onClick={submit}
            disabled={!canSubmit || submitting}
            className={`w-full inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 font-semibold shadow-sm transition-all ${
              canSubmit && !submitting ? 'bg-gradient-to-br from-emerald-500 to-teal-500 hover:brightness-110 text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
            data-testid="growth-submit"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Update growth today
          </button>
        </div>

        {result && (
          <div className={`rounded-2xl border-2 ${BAND_COLOR[result.band_label] || 'border-gray-200 bg-gray-50'} p-5 mb-4`} data-testid="growth-result">
            <p className="text-[11px] font-semibold uppercase tracking-widest mb-1">Trend</p>
            <h3 className="text-xl font-bold text-gray-900" data-testid="growth-band">{result.band_label} · BMI {result.bmi}</h3>
            {result.delta && (
              <p className="text-xs text-gray-700 mt-2 inline-flex items-center gap-1.5" data-testid="growth-delta">
                {result.delta.height_cm > 0 ? <ArrowUpRight className="h-3.5 w-3.5 text-emerald-700" /> : result.delta.height_cm < 0 ? <ArrowDownRight className="h-3.5 w-3.5 text-rose-700" /> : <Minus className="h-3.5 w-3.5 text-gray-500" />}
                {result.delta.height_cm > 0 ? '+' : ''}{result.delta.height_cm}cm height · {result.delta.weight_kg > 0 ? '+' : ''}{result.delta.weight_kg}kg weight since last entry
              </p>
            )}
            <p className="text-xs text-gray-700 mt-2">Recheck suggestion: ~{result.recheck_in_weeks} weeks</p>
            <div className="flex items-center gap-1.5 mt-3 text-xs text-emerald-700">
              <Save className="h-3 w-3" /> Saved
            </div>
          </div>
        )}

        {recent.length > 0 && (
          <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-3">Recent entries</p>
            <ul className="space-y-2" data-testid="growth-recent">
              {recent.map((r) => (
                <li key={r.id} className="text-sm text-gray-700 flex items-center justify-between">
                  <span>{new Date(r.created_at).toLocaleDateString()}</span>
                  <span className="text-gray-900 font-medium">{r.height_cm}cm · {r.weight_kg}kg · BMI {r.bmi}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <p className="text-[11px] text-gray-400 text-center mt-6">
          Trend bands are a starting guide — your pediatrician's percentile chart is the standard.
        </p>
      </div>
    </div>
  );
};

export default Growth;
